/**
 * Modal dialog with the "popin" markup. Built on the native <dialog> element:
 * focus trapping, Escape and the backdrop come from the browser. The content
 * uses the `.popin-*` classes of the stylesheet.
 */
import { useEffect, useId, useRef, type ReactNode } from 'react';
import closeIcon from '@/assets/images/close.svg';
import { dialogCancelHandler } from './dialog-cancel';

export interface DialogProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** CSS width of the panel (default 900px). */
  width?: string;
  /** Tall content area (85vh), as the store dialog had. */
  tall?: boolean;
  testId?: string | undefined;
  contentOverflow?: 'auto' | 'visible';
}

export function Dialog({
  title,
  onClose,
  children,
  width = '900px',
  tall = false,
  testId,
  contentOverflow = 'auto',
}: Readonly<DialogProps>) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    // Rendered means open; removing the element from the document closes it
    // without a `close` event (closing it here would fire one and call onClose).
    if (!dialog.open) dialog.showModal();
  }, []);

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onCancel={dialogCancelHandler(onClose)}
      onClose={onClose}
      className="app-dialog"
      style={{ width, maxWidth: 'calc(100vw - 30px)' }}
      data-testid={testId}
    >
      <div className="popin-container">
        <div className="popin">
          <div className="popin-head">
            <h2 className="popin-heading" id={titleId}>
              {title}
            </h2>
            <button
              type="button"
              className="popin-close"
              onClick={onClose}
              aria-label="Close"
              data-testid={testId ? `${testId}-close-button` : undefined}
            >
              <img src={closeIcon} alt="" />
            </button>
          </div>
          <div
            className={`dialog-content ${tall ? 'dialog-content-tall' : ''}`}
            role="region"
            aria-label={title}
            style={{ overflow: contentOverflow }}
          >
            {children}
          </div>
        </div>
      </div>
    </dialog>
  );
}
